"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { Expand } from "lucide-react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

export type CarouselImage = {
  src: string;
  alt: string;
  /** Set for a video slide — src should point to a video file (e.g. .mp4) */
  video?: boolean;
};

type ImageCarouselProps = {
  images: CarouselImage[];
  /** Sizing classes for the media box, e.g. "aspect-[4/3] lg:h-full" — no rounding here, see radiusClassName */
  className?: string;
  /** Corner radius, applied to both the outer box and each slide directly, e.g. "rounded-3xl" */
  radiusClassName?: string;
  priority?: boolean;
};

function CarouselDots({ count, current }: { count: number; current: number }) {
  if (count < 2) return null;

  return (
    <div className="pointer-events-none absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={cn(
            "h-1.5 rounded-full bg-white/60 transition-all",
            i === current ? "w-4 bg-white" : "w-1.5"
          )}
        />
      ))}
    </div>
  );
}

export default function ImageCarousel({
  images,
  className,
  radiusClassName = "rounded-3xl",
  priority = false,
}: ImageCarouselProps) {
  const t = useTranslations("Carousel");
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxApi, setLightboxApi] = useState<CarouselApi>();
  const [lightboxCurrent, setLightboxCurrent] = useState(0);

  const hasMultiple = images.length > 1;

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!lightboxApi) return;
    setLightboxCurrent(lightboxApi.selectedScrollSnap());
    const onSelect = () => setLightboxCurrent(lightboxApi.selectedScrollSnap());
    lightboxApi.on("select", onSelect);
    return () => {
      lightboxApi.off("select", onSelect);
    };
  }, [lightboxApi]);

  const openLightbox = useCallback((index: number) => {
    setCurrent(index);
    setLightboxOpen(true);
  }, []);

  return (
    <>
      {/* Inline carousel */}
      <div className={cn("group relative overflow-hidden", radiusClassName, className)}>
        <Carousel setApi={setApi} opts={{ loop: hasMultiple }} className="h-full">
          <CarouselContent className="-ml-0 h-full">
            {images.map((img, i) => (
              <CarouselItem key={i} className="h-full pl-0">
                {/* radiusClassName + overflow-hidden are repeated here, not just on the outer
                    wrapper above: embla applies its translate3d transform to an ancestor of this
                    button, and WebKit/Chrome can fail to clip a GPU-composited transformed
                    subtree from further up the tree — clipping locally, on the element carried by
                    the transform rather than one trying to clip across it, avoids that bug. */}
                <button
                  onClick={() => openLightbox(i)}
                  className={cn("relative block h-full w-full overflow-hidden", radiusClassName)}
                  aria-label={img.video ? t("openVideoAria", { alt: img.alt }) : t("openImageAria", { alt: img.alt })}
                >
                  {img.video ? (
                    <video
                      src={img.src}
                      className="absolute inset-0 h-full w-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      aria-label={img.alt}
                    />
                  ) : (
                    <Image
                      src={img.src}
                      alt={img.alt}
                      fill
                      className="object-cover"
                      priority={priority && i === 0}
                    />
                  )}
                  <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                    <Expand className="h-4 w-4" />
                  </span>
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>

          {hasMultiple && (
            <>
              <CarouselPrevious className="left-3 border-none bg-white/90 text-[#18181b] shadow-md hover:bg-white disabled:opacity-0" />
              <CarouselNext className="right-3 border-none bg-white/90 text-[#18181b] shadow-md hover:bg-white disabled:opacity-0" />
            </>
          )}
        </Carousel>

        <CarouselDots count={images.length} current={current} />
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          showCloseButton
          className="max-w-4xl border-none bg-transparent p-0 shadow-none sm:max-w-4xl [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:bg-black/50 [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:text-white [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:hover:bg-black/70"
        >
          <DialogTitle className="sr-only">{t("galleryTitle")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("galleryDescription")}
          </DialogDescription>
          <div className="relative" key={current}>
            <Carousel
              setApi={setLightboxApi}
              opts={{ loop: hasMultiple, startIndex: current }}
              className="w-full"
            >
              <CarouselContent className="-ml-0 items-center">
                {images.map((img, i) => (
                  <CarouselItem key={i} className="flex h-[60vh] items-center justify-center pl-0 sm:h-[75vh]">
                    <div className="h-full overflow-hidden rounded-2xl [contain:paint]">
                      {img.video ? (
                        <video
                          src={img.src}
                          className="h-full w-auto max-w-full object-contain"
                          controls
                          autoPlay
                          muted
                          loop
                          playsInline
                          aria-label={img.alt}
                        />
                      ) : (
                        // eslint-disable-next-line @next/next/no-img-element -- natural aspect ratio, unknown ahead of time
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="h-full w-auto max-w-full object-contain"
                        />
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {hasMultiple && (
                <>
                  <CarouselPrevious className="left-2 border-none bg-white/90 text-[#18181b] shadow-md hover:bg-white sm:-left-12" />
                  <CarouselNext className="right-2 border-none bg-white/90 text-[#18181b] shadow-md hover:bg-white sm:-right-12" />
                </>
              )}
            </Carousel>

            <CarouselDots count={images.length} current={lightboxCurrent} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
